import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ClockCounterClockwise, CheckCircle, XCircle, Warning } from '@phosphor-icons/react';

import { PageSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODULE_COLORS = {
  Leads: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  Customers: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  Products: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  Quotations: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
};

const ImportHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { fetchHistory(); }, []);

  const fetchHistory = async () => {
    try {
      const resp = await axios.get(`${API}/import-history`);
      setHistory(resp.data);
    } catch (error) {
      console.error(error);
    } finally { setLoading(false); }
  };

  if (loading) return <PageSkeleton />;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="import-history-page">
      <div className="mb-8">
        <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>
          Import History
        </h1>
        <p className="text-[#6D8E87]">Track all bulk upload operations</p>
      </div>

      {history.length === 0 ? (
        <div className="medical-card p-12 text-center" data-testid="no-history">
          <ClockCounterClockwise size={48} className="text-[#D5E8E4] mx-auto mb-3" />
          <h3 className="text-xl font-semibold text-[#052A23] mb-2">No Import History</h3>
          <p className="text-[#6D8E87]">Bulk upload records will appear here.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="history-list">
          {history.map((rec) => {
            const mc = MODULE_COLORS[rec.module] || MODULE_COLORS.Leads;
            const totalSuccess = (rec.created || 0) + (rec.updated || 0);
            const hasErrors = (rec.error_count || 0) > 0;
            const isExpanded = expandedId === rec.id;

            return (
              <div key={rec.id} className="medical-card overflow-hidden" data-testid={`history-${rec.id}`}>
                <div className="p-5 flex items-center gap-4 cursor-pointer hover:bg-[#FAFFFE] transition"
                  onClick={() => setExpandedId(isExpanded ? null : rec.id)}>
                  {/* Status icon */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasErrors && totalSuccess === 0 ? 'bg-red-100' : hasErrors ? 'bg-amber-100' : 'bg-green-100'}`}>
                    {hasErrors && totalSuccess === 0 ? <XCircle size={22} className="text-red-500" weight="bold" /> :
                     hasErrors ? <Warning size={22} className="text-amber-500" weight="bold" /> :
                     <CheckCircle size={22} className="text-green-500" weight="bold" />}
                  </div>

                  {/* Module badge + action */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${mc.bg} ${mc.text}`}>{rec.module}</span>
                      <span className="text-sm font-medium text-[#052A23]">{rec.action}</span>
                      {rec.filename && <span className="text-xs text-[#6D8E87]">({rec.filename})</span>}
                    </div>
                    <p className="text-xs text-[#6D8E87]">by {rec.user_name} ({rec.user_role})</p>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm">
                    {(rec.created || 0) > 0 && (
                      <span className="text-green-600 font-semibold">{rec.created} created</span>
                    )}
                    {(rec.updated || 0) > 0 && (
                      <span className="text-blue-600 font-semibold">{rec.updated} updated</span>
                    )}
                    {(rec.skipped || 0) > 0 && (
                      <span className="text-[#6D8E87]">{rec.skipped} skipped</span>
                    )}
                    {hasErrors && (
                      <span className="text-red-500 font-semibold">{rec.error_count} error{rec.error_count > 1 ? 's' : ''}</span>
                    )}
                  </div>

                  {/* Timestamp */}
                  <div className="text-xs text-[#6D8E87] w-36 text-right">
                    {new Date(rec.created_at).toLocaleString()}
                  </div>
                </div>

                {/* Expanded errors */}
                {isExpanded && rec.errors?.length > 0 && (
                  <div className="px-5 pb-4 pt-0 border-t border-[#D5E8E4]">
                    <p className="text-xs font-semibold text-red-600 mt-3 mb-2">Errors:</p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {rec.errors.map((err, i) => (
                        <p key={i} className="text-xs text-red-500 pl-3 border-l-2 border-red-200">{err}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ImportHistory;
